import { useTranslation } from 'react-i18next'
import { fmt, pct } from './format'

const STRENGTH_STYLE = {
  strong: 'font-semibold', moderate: 'font-medium', weak: '', none: 'text-gray-500',
  not_enough_data: 'text-gray-500 italic', present: 'font-medium', coupled: '', decoupled: 'font-medium',
}

function sampleSize(n, t) {
  if (!n) return '—'
  if (n.group != null) return t('systemView.couplings.nGroups', { group: n.group, rest: n.rest })
  if (n.pairs != null) return t('systemView.couplings.nPairs', { pairs: n.pairs })
  return Object.values(n).join(' / ')
}

function Flags({ coupling }) {
  const { t } = useTranslation()
  const flags = []
  if (coupling.robust_agrees === false) flags.push(<span key="robust" data-testid="flag-not-robust">{t('systemView.couplings.notRobust')}</span>)
  if (coupling.constant_variable) {
    flags.push(<span key="constant" data-testid="flag-constant">{t('systemView.couplings.nearlyConstant', {
      variable: t(`systemView.variables.${coupling.constant_variable.variable}`),
    })}</span>)
  }
  const note = coupling.weekday_note
  if (note) {
    flags.push(<span key="weekday" data-testid="flag-weekday">{t(`systemView.couplings.weekdayNotes.${note.code}`, {
      group: pct(note.weekend_share_group), rest: pct(note.weekend_share_rest), r: fmt(note.r_with_weekend, 2),
    })}</span>)
  }
  if (flags.length === 0) return null
  return <div className="flex flex-col gap-0.5 text-amber-700">{flags}</div>
}

/** VH-222: all twelve couplings, including null findings and "not enough data". */
export default function CouplingTable({ couplings = [] }) {
  const { t } = useTranslation()
  return (
    <section className="space-y-2" data-testid="coupling-table">
      <h3 className="text-base font-semibold">{t('systemView.couplings.title')}</h3>
      <p className="text-xs text-gray-600">{t('systemView.couplings.associationNote')}</p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-left text-gray-600">
              <th className="py-1 pr-3">{t('systemView.couplings.pair')}</th>
              <th className="py-1 pr-3">{t('systemView.couplings.effect')}</th>
              <th className="py-1 pr-3">{t('systemView.couplings.strength')}</th>
              <th className="py-1 pr-3">{t('systemView.couplings.n')}</th>
              <th className="py-1 pr-3">{t('systemView.couplings.notes')}</th>
            </tr>
          </thead>
          <tbody>
            {couplings.map((c) => {
              const muted = c.null_finding || !c.sufficient_data
              return (
                <tr key={c.code} className={`border-t align-top ${muted ? 'text-gray-500' : ''}`}
                  data-testid={`coupling-${c.code}`} data-muted={String(muted)}>
                  <td className="py-1 pr-3">
                    <div>{t(`systemView.couplings.codes.${c.code}`)}</div>
                    {c.lag_days > 0 && <div className="text-gray-500">{t('systemView.couplings.lag', { days: c.lag_days })}</div>}
                  </td>
                  <td className="py-1 pr-3">
                    {c.effect == null ? '—' : (
                      <span>
                        {t(`systemView.methods.${c.method}`, { value: fmt(c.effect, 2) })}
                        {c.difference != null && <span className="block text-gray-500">{t('systemView.couplings.difference', { value: fmt(c.difference), unit: c.unit || '' })}</span>}
                      </span>
                    )}
                  </td>
                  <td className={`py-1 pr-3 ${STRENGTH_STYLE[c.strength] || ''}`}>
                    {c.null_finding ? t('systemView.couplings.nullFinding') : t(`systemView.strength.${c.strength}`)}
                  </td>
                  <td className="py-1 pr-3">{sampleSize(c.n, t)}</td>
                  <td className="py-1 pr-3"><Flags coupling={c} /></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
